import { showSuccessNotification } from "molecules/notifications/notificationEvents";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { isEditableTarget } from "src/common/utils/isEditableTarget.js";

const SESSION_KEY = "devToolsUnlocked";
const CHANGE_EVENT = "dev-tools-unlock-change";
const REQUIRED_PRESSES = 3;
const RESET_TIMEOUT_MS = 1500;

const readUnlocked = () => {
	try {
		return window.sessionStorage.getItem(SESSION_KEY) === "true";
	} catch {
		return false;
	}
};

/** Exported for tests, which drive the gate through the real store rather than
 * mocking the hook. Production code writes it only via the `§§§` listener. */
export const setDevToolsUnlocked = (unlocked: boolean) => {
	try {
		window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(unlocked));
	} catch {
		return;
	}
	window.dispatchEvent(new Event(CHANGE_EVENT));
};

const subscribe = (onStoreChange: () => void) => {
	window.addEventListener(CHANGE_EVENT, onStoreChange);
	return () => {
		window.removeEventListener(CHANGE_EVENT, onStoreChange);
	};
};

// A disabled reader can never turn true, so it neither subscribes nor reads storage.
const subscribeToNothing = () => () => undefined;
const readLocked = () => false;

/** Reads the unlock state. Safe to call from any number of components at once —
 * it only subscribes, so it can gate a page and the sidebar simultaneously.
 * The `§§§` handler lives in `useDevToolsUnlockListener`, mounted once. */
export const useDevToolsUnlocked = (enabled: boolean) =>
	useSyncExternalStore(
		enabled ? subscribe : subscribeToNothing,
		enabled ? readUnlocked : readLocked,
	);

/** WHY mounted exactly once (`DevToolsUnlockListener` in `ProtectedApp`): each
 * call registers a `keydown` listener that toggles, so a second mounted call
 * would flip the same `§§§` twice and net to nothing. */
export const useDevToolsUnlockListener = (enabled: boolean) => {
	const { t } = useTranslation();
	const pressCountRef = useRef(0);
	const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	useEffect(() => {
		if (!enabled) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "§") return;
			if (isEditableTarget(e.target)) return;

			pressCountRef.current += 1;

			clearTimeout(resetTimerRef.current);
			resetTimerRef.current = setTimeout(() => {
				pressCountRef.current = 0;
			}, RESET_TIMEOUT_MS);

			if (pressCountRef.current >= REQUIRED_PRESSES) {
				pressCountRef.current = 0;
				clearTimeout(resetTimerRef.current);
				// Read-through rather than a cached ref: the store is the one source of truth.
				const nowUnlocked = !readUnlocked();
				setDevToolsUnlocked(nowUnlocked);
				showSuccessNotification({
					message: nowUnlocked
						? t("Dev tools unlocked")
						: t("Dev tools locked"),
				});
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			clearTimeout(resetTimerRef.current);
		};
	}, [enabled, t]);
};
